/* ILS design system behaviour: header menus, search, drawer, gallery, in-page nav, filter sheet. */
(() => {
  const qa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- header menus (details elements) ---------- */
  function initHeader(header) {
    if (!header || header.dataset.sReady) return;
    header.dataset.sReady = 'true';
    const menus = qa('details[data-s-menu]', header);
    const closeAll = (except) => menus.forEach((d) => { if (d !== except) d.open = false; });
    menus.forEach((d) => {
      d.addEventListener('toggle', () => { if (d.open) { closeAll(d); closeSearch(); } });
      d.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && d.open) { d.open = false; d.querySelector('summary').focus(); }
      });
      d.addEventListener('focusout', (e) => { if (d.open && !d.contains(e.relatedTarget) && e.relatedTarget) d.open = false; });
    });
    document.addEventListener('click', (e) => { if (!header.contains(e.target)) { closeAll(); } });

    /* search panel */
    const searchBtn = header.querySelector('[data-s-search-toggle]');
    const search = searchBtn && document.getElementById(searchBtn.getAttribute('aria-controls'));
    function closeSearch() { if (search && !search.hidden) { search.hidden = true; searchBtn.setAttribute('aria-expanded', 'false'); } }
    if (searchBtn && search) {
      searchBtn.addEventListener('click', () => {
        const open = search.hidden;
        closeAll();
        search.hidden = !open;
        searchBtn.setAttribute('aria-expanded', String(open));
        if (open) search.querySelector('input[type="search"]')?.focus();
      });
      search.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeSearch(); searchBtn.focus(); } });
      document.addEventListener('click', (e) => { if (!search.hidden && !search.contains(e.target) && e.target !== searchBtn && !searchBtn.contains(e.target)) closeSearch(); });
    }

    /* mobile drawer */
    const drawerBtn = header.querySelector('[data-s-drawer-toggle]');
    const drawer = drawerBtn && document.getElementById(drawerBtn.getAttribute('aria-controls'));
    if (drawerBtn && drawer) {
      const setDrawer = (open) => {
        drawer.hidden = !open;
        drawerBtn.setAttribute('aria-expanded', String(open));
        document.body.classList.toggle('s-locked', open);
        drawerBtn.querySelector('.s-visually-hidden').textContent = open ? 'Close menu' : 'Menu';
        drawerBtn.querySelector('svg path')?.setAttribute('d', open ? 'M6 6l12 12M18 6 6 18' : 'M4 8h16M4 16h16');
        if (open) drawer.querySelector('summary, a')?.focus();
      };
      drawerBtn.addEventListener('click', () => setDrawer(drawer.hidden));
      drawer.addEventListener('keydown', (e) => { if (e.key === 'Escape') { setDrawer(false); drawerBtn.focus(); } });
      matchMedia('(min-width: 1024px)').addEventListener('change', (m) => { if (m.matches) setDrawer(false); });
    }
  }

  /* ---------- product gallery ---------- */
  function initGallery(root) {
    if (root.dataset.sReady) return;
    root.dataset.sReady = 'true';
    const main = root.querySelector('[data-s-main]');
    const zoom = root.querySelector('[data-s-zoom]');
    const thumbs = qa('[data-s-thumb]', root);
    thumbs.forEach((btn) => btn.addEventListener('click', () => {
      if (!main) return;
      main.srcset = btn.dataset.srcset || '';
      main.src = btn.dataset.src;
      main.alt = btn.dataset.alt || '';
      main.className = btn.dataset.class || main.className;
      main.dataset.imageId = btn.dataset.imageId || '';
      if (zoom) zoom.href = btn.dataset.full;
      thumbs.forEach((t) => t.setAttribute('aria-current', String(t === btn)));
      root.dispatchEvent(new CustomEvent('ils:gallery-change', { bubbles: true, detail: { imageId: btn.dataset.imageId } }));
    }));
  }

  /* ---------- hotspots: hidden until asked for; only on their source image ---------- */
  const setLabel = (el, text) => { const t = el.querySelector('[data-label]'); if (t) t.textContent = text; else el.textContent = text; };
  function initHotspots(root) {
    if (root.dataset.sReady) return;
    root.dataset.sReady = 'true';
    const gallery = root.closest('[data-s-gallery]');
    const markers = root.querySelector('[data-s-markers]');
    const list = (root.closest('[data-ils-product]') || document).querySelector('[data-s-hotspot-root]');
    const toggle = list?.querySelector('[data-s-features-toggle]');
    let on = false;
    const onSource = () => {
      const cur = gallery?.querySelector('[data-s-main]')?.dataset.imageId;
      return !cur || cur === root.dataset.imageId;
    };
    const render = () => {
      const ok = onSource();
      if (markers) markers.hidden = !(on && ok);
      if (toggle) {
        toggle.disabled = !ok;
        toggle.setAttribute('aria-pressed', String(on && ok));
        setLabel(toggle, on && ok ? 'Hide features' : 'Show features on the photo');
      }
    };
    toggle?.addEventListener('click', () => { on = !on; render(); });
    gallery?.addEventListener('ils:gallery-change', render);
    qa('[data-s-hotspot]', root).forEach((btn) => btn.addEventListener('click', () => {
      const d = document.getElementById(btn.getAttribute('aria-controls'));
      if (!d) return;
      const open = !d.open;
      qa('details', list || document).forEach((x) => { if (x !== d) x.open = false; });
      d.open = open;
      if (open) d.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }));
    qa('details', list || root).forEach((d) => d.addEventListener('toggle', () => {
      const b = root.querySelector(`[aria-controls="${d.id}"]`);
      if (b) b.setAttribute('aria-expanded', String(d.open));
    }));
    render();
  }

  /* ---------- in-page nav highlight ---------- */
  function initSubnav(nav) {
    if (nav.dataset.sReady) return;
    nav.dataset.sReady = 'true';
    const links = qa('a[href^="#"]', nav);
    const targets = links.map((a) => document.getElementById(a.getAttribute('href').slice(1))).filter(Boolean);
    if (!('IntersectionObserver' in window) || !targets.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) links.forEach((a) => a.setAttribute('aria-current', String(a.getAttribute('href') === '#' + en.target.id)));
      });
    }, { rootMargin: '-45% 0px -50% 0px' });
    targets.forEach((t) => io.observe(t));
  }

  /* ---------- filter sheet ---------- */
  function initSheet(btn) {
    if (btn.dataset.sReady) return;
    btn.dataset.sReady = 'true';
    const sheet = document.getElementById(btn.getAttribute('aria-controls'));
    if (!sheet) return;
    const open = () => { sheet.hidden = false; btn.setAttribute('aria-expanded', 'true'); document.body.classList.add('s-locked'); sheet.querySelector('[data-s-sheet-close]')?.focus(); };
    const close = () => { sheet.hidden = true; btn.setAttribute('aria-expanded', 'false'); document.body.classList.remove('s-locked'); btn.focus(); };
    btn.addEventListener('click', open);
    qa('[data-s-sheet-close]', sheet).forEach((c) => c.addEventListener('click', close));
    sheet.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') { // keep focus inside the open sheet
        const f = qa('a[href], button:not([disabled]), input:not([disabled]), select, [tabindex]:not([tabindex="-1"])', sheet).filter((x) => x.offsetParent !== null);
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    });
  }

  /* ---------- footer groups: open on desktop, collapsed by default on phones ---------- */
  const footMq = matchMedia('(max-width: 599px)');
  function syncFooter() {
    qa('[data-s-footcol]').forEach((d) => {
      d.open = !footMq.matches;
      const s = d.querySelector('summary');
      if (s) { if (footMq.matches) s.removeAttribute('tabindex'); else s.setAttribute('tabindex', '-1'); }
    });
  }
  function initFooter() {
    if (document.documentElement.dataset.sFooter) return;
    document.documentElement.dataset.sFooter = 'true';
    syncFooter();
    footMq.addEventListener('change', syncFooter);
    qa('[data-s-footcol]').forEach((d) => d.addEventListener('toggle', () => { if (!footMq.matches && !d.open) d.open = true; }));
  }

  /* ---------- brand family line-up: equal height, bounded by available width ---------- */
  function fitFamily(stage) {
    const row = stage.querySelector('.s-family__row'); if (!row) return;
    const items = qa('.s-family__item', row); if (!items.length) return;
    const ratios = items.map((it) => { const im = it.querySelector('img'); const w = +im.getAttribute('width'), h = +im.getAttribute('height'); return w && h ? w / h : 0.75; });
    const cs = getComputedStyle(stage);
    const W = stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const H = stage.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    const gap = W * 0.03 * (items.length - 1);
    const h = Math.min(H * 0.82, (W - gap) / ratios.reduce((a, b) => a + b, 0));
    items.forEach((it, i) => { it.style.flex = 'none'; it.style.height = h + 'px'; it.style.width = (h * ratios[i]) + 'px'; });
  }
  function initFamily(stage) {
    if (stage.dataset.sReady) return; stage.dataset.sReady = 'true';
    fitFamily(stage);
    if ('ResizeObserver' in window) new ResizeObserver(() => fitFamily(stage)).observe(stage);
  }

  /* Scrollable tab rows: fade only an edge that hides more tabs, and bring the current tab into view. */
  function initTabs(nav) {
    if (nav.dataset.sReady) return; nav.dataset.sReady = 'true';
    const update = () => {
      const max = nav.scrollWidth - nav.clientWidth;
      const x = nav.scrollLeft;
      nav.dataset.fade = max <= 1 ? 'none' : x <= 1 ? 'end' : x >= max - 1 ? 'start' : 'both';
    };
    const cur = nav.querySelector('[aria-current="page"]');
    if (cur && nav.scrollWidth > nav.clientWidth) {
      const off = cur.getBoundingClientRect().left - nav.getBoundingClientRect().left;
      if (off + cur.offsetWidth > nav.clientWidth) nav.scrollLeft += off - (nav.clientWidth - cur.offsetWidth) / 2;
    }
    nav.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(nav);
    update();
  }

  function init(root = document) {
    initFooter();
    qa('.s-tabs', root).forEach(initTabs);
    qa('[data-s-header]', root).forEach(initHeader);
    qa('.s-family', root).forEach(initFamily);
    qa('[data-s-gallery]', root).forEach(initGallery);
    qa('[data-s-hotspots]', root).forEach(initHotspots);
    qa('[data-s-subnav]', root).forEach(initSubnav);
    qa('[data-s-sheet-open]', root).forEach(initSheet);
    qa('form[data-s-autosubmit]', root).forEach((f) => { if (f.dataset.sReady) return; f.dataset.sReady = 'true'; f.addEventListener('change', () => (f.requestSubmit ? f.requestSubmit() : f.submit())); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
